import React from 'react';
import { Alert, Button, Typography } from 'antd';
import { S } from './ui';

const { Text } = Typography;

/** What the website usually takes to rebuild and go out to the CDN. */
const REBUILD_MINUTES = 5;

function minutesSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 60000));
}

/**
 * What happened to the last publish, stated until it stops being true.
 *
 * Publishing used to report itself in a toast that vanished after a few seconds, leaving
 * a screen that looked identical before and after. Editors pressed the button again, which
 * is the reasonable thing to do when nothing on the page says otherwise.
 *
 * The panel deliberately does not claim the page is live. Nothing here can observe that:
 * the site is a static build on another origin, so the CMS cannot read it, and the
 * platform only knows that a rebuild was requested. Saying "published, rebuilding, usually
 * about five minutes" is the true version, and a claim of completion we cannot check would
 * be worse than none.
 */
const PublishStatus: React.FC<{
  deploy: { status: string | null; detail: string | null; requested_at: string | null } | null;
  version: number | null;
  siteLabel: string;
  siteUrl: string;
  canRetry: boolean;
  retrying: boolean;
  onRetry: () => void;
}> = ({ deploy, version, siteLabel, siteUrl, canRetry, retrying, onRetry }) => {
  // Re-render on a timer so the elapsed line stays honest while the page sits open.
  const [, tick] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  if (!deploy?.status || version == null) return null;

  const view = (
    <Button size="small" href={siteUrl} target="_blank" rel="noopener noreferrer">
      View the page
    </Button>
  );
  const retry = canRetry ? (
    <Button size="small" loading={retrying} onClick={onRetry}>
      Ask it to rebuild
    </Button>
  ) : undefined;

  if (deploy.status === 'dispatched') {
    const mins = minutesSince(deploy.requested_at);
    const elapsed =
      mins === null ? '' : mins < 1 ? ' Started just now.' : ` Started ${mins} minute${mins === 1 ? '' : 's'} ago.`;
    const late = mins !== null && mins > REBUILD_MINUTES * 3;
    return (
      <Alert
        className="mb-4"
        type={late ? 'warning' : 'info'}
        showIcon
        message={`Version ${version} is published. ${siteLabel} is being updated.`}
        description={
          <span>
            This usually takes about {REBUILD_MINUTES} minutes.{elapsed} You do not need to
            do anything, and you can close this page.
            {late && (
              <>
                {' '}
                <Text type="warning">
                  It has been longer than usual, so it is worth asking for the update again.
                </Text>
              </>
            )}
            <div style={{ marginTop: S.sm, display: 'flex', gap: S.sm }}>
              {view}
              {late ? retry : null}
            </div>
          </span>
        }
      />
    );
  }

  const message =
    deploy.status === 'failed'
      ? `Version ${version} is published, but ${siteLabel} was not updated.`
      : `Version ${version} is published. ${siteLabel} has not been asked to update.`;

  return (
    <Alert
      className="mb-4"
      type="warning"
      showIcon
      message={message}
      description={
        <span>
          The page still shows the previous version. {deploy.detail}
          <div style={{ marginTop: S.sm, display: 'flex', gap: S.sm }}>
            {retry}
            {view}
          </div>
        </span>
      }
    />
  );
};

export default PublishStatus;
